import React, { createContext, useContext, useEffect, useState } from 'react'
import type { User, Session } from '@supabase/supabase-js'
import { supabase } from '../lib/supabase'

interface AuthContextType {
  user: User | null
  session: Session | null
  loading: boolean
  primeiroAcesso: boolean
  signIn: (email: string, password: string) => Promise<{ error: any; primeiroAcesso?: boolean }>
  signOut: () => Promise<void>
  isAuthenticated: boolean
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

interface AuthProviderProps {
  children: React.ReactNode
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null)
  const [session, setSession] = useState<Session | null>(null)
  const [loading, setLoading] = useState(true)
  const [primeiroAcesso, setPrimeiroAcesso] = useState(false)
  const [isLoggingOut, setIsLoggingOut] = useState(false)

  useEffect(() => {
    let mounted = true
    
    const initAuth = async () => {
      try {
        console.log('🔐 Iniciando autenticação...')
        console.log('🌍 Environment:', process.env.NODE_ENV)
        console.log('🔗 Supabase URL:', process.env.VITE_SUPABASE_URL)
        
        const { data: { session }, error } = await supabase.auth.getSession()
        
        if (error) {
          console.error('❌ Erro ao obter sessão:', error)
          throw error
        }
        
        console.log('📋 Sessão obtida:', session ? 'Usuário logado' : 'Sem sessão')
        
        if (mounted) {
          setSession(session)
          setUser(session?.user ?? null)
          setLoading(false)
        }
      } catch (error) {
        console.error('❌ Erro na inicialização da auth:', error)
        if (mounted) {
          setSession(null)
          setUser(null)
          setLoading(false)
        }
      }
    }
    
    // Timeout de segurança para evitar loading infinito
    const timeout = setTimeout(() => {
      if (mounted) {
        console.warn('⚠️ Auth timeout - forçando loading = false')
        console.log('🔍 Estado no timeout - mounted:', mounted, 'loading:', loading)
        setLoading(false)
      }
    }, 3000)

    // Escutar mudanças de autenticação
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, session) => {
      console.log('🔄 Auth state change:', event, session ? 'com sessão' : 'sem sessão')
      
      // Ignorar eventos durante logout para prevenir loop
      if (isLoggingOut) {
        console.log('⏭️ Ignorando evento durante logout')
        return
      }
      
      if (mounted) {
        setSession(session)
        setUser(session?.user ?? null)
        setLoading(false)
      }

      // Atualizar último acesso quando usuário faz login
      if (event === 'SIGNED_IN' && session?.user?.email) {
        try {
          await supabase
            .from('usuarios_gestao')
            .update({ ultimo_acesso: new Date().toISOString() })
            .eq('email', session.user.email)
        } catch (error) {
          console.error('❌ Erro ao atualizar último acesso:', error)
        }
      }
    })

    initAuth()

    return () => {
      mounted = false
      clearTimeout(timeout)
      subscription.unsubscribe()
    }
  }, [])

  const signIn = async (email: string, password: string) => {
    const { error, data } = await supabase.auth.signInWithPassword({
      email,
      password,
    })
    
    if (error) {
      return { error }
    }
    
    // Buscar dados do usuário na tabela usuarios_gestao
    if (data.user) {
      try {
        const response = await fetch(`/api/gestao/usuarios?email=${encodeURIComponent(email)}`)
        if (response.ok) {
          const userData = await response.json()
          const isPrimeiroAcesso = userData.primeiro_acesso === true
          setPrimeiroAcesso(isPrimeiroAcesso)
          return { error: null, primeiroAcesso: isPrimeiroAcesso }
        }
      } catch (err) {
        console.error('Erro ao buscar dados do usuário:', err)
      }
    }
    
    return { error: null, primeiroAcesso: false }
  }

  const signOut = async () => {
    // Ativar flag para prevenir loop infinito
    setIsLoggingOut(true)
    
    console.log('🔓 Limpando sessão do Supabase...')
    
    // Usar scope: 'local' para limpar apenas a sessão local
    const { error } = await supabase.auth.signOut({ scope: 'local' })
    
    if (error) {
      console.error('Erro ao fazer signOut:', error)
    }
    
    // Limpar estado local
    setUser(null)
    setSession(null)
    setPrimeiroAcesso(false)
    
    console.log('✅ Sessão limpa completamente')
  }

  const value = {
    user,
    session,
    loading,
    primeiroAcesso,
    signIn,
    signOut,
    isAuthenticated: !!user,
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}
